import {
  DEFAULT_ROUND_COUNT,
  FISH_PRODUCT_IDS,
  type FishMarketByProduct,
  type FishProductId,
  type FishShopV2Phase,
  type FishShopV2Round,
  type FishShopV2State,
  type FishTicketsByProduct,
  type InitGameOptions,
  MAX_PLAYER_COUNT,
  MIN_PLAYER_COUNT,
  SALES_TICKETS_PER_PLAYER,
} from "./types";

function getDefaultSeed(playerIds: readonly string[]): number {
  const source = playerIds.join("|");
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function assertValidPlayerIds(
  playerIds: readonly string[],
  hostPlayerId: string,
): void {
  if (
    playerIds.length < MIN_PLAYER_COUNT ||
    playerIds.length > MAX_PLAYER_COUNT
  ) {
    throw new Error(
      `Fish Shop v2 requires ${MIN_PLAYER_COUNT}-${MAX_PLAYER_COUNT} players`,
    );
  }

  const deduped = new Set(playerIds);
  if (deduped.size !== playerIds.length) {
    throw new Error("Player IDs must be unique");
  }

  if (!deduped.has(hostPlayerId)) {
    throw new Error("Host player must be included in player IDs");
  }
}

export function createEmptyTicketsByProduct(): FishTicketsByProduct {
  return {
    HAIRTAIL: 0,
    MACKEREL: 0,
    SQUID: 0,
  };
}

export function createEmptyMarket(): FishMarketByProduct {
  return {
    HAIRTAIL: { productId: "HAIRTAIL", sellers: [], soldTicketCount: 0 },
    MACKEREL: { productId: "MACKEREL", sellers: [], soldTicketCount: 0 },
    SQUID: { productId: "SQUID", sellers: [], soldTicketCount: 0 },
  };
}

export function createRound(
  roundNumber: number,
  phase: FishShopV2Phase = "TICKET_ALLOCATION",
): FishShopV2Round {
  return {
    roundNumber,
    phase,
    allocations: {},
    market: createEmptyMarket(),
    priceSubmissions: {},
    secretSelections: {},
    productResults: [],
    incomeByPlayerId: {},
    confirmedPlayerIds: [],
  };
}

export function normalizeTicketsByProduct(
  ticketsByProduct: Readonly<Partial<Record<FishProductId, number>>>,
): FishTicketsByProduct {
  return Object.fromEntries(
    FISH_PRODUCT_IDS.map((productId) => [
      productId,
      ticketsByProduct[productId] ?? 0,
    ]),
  ) as FishTicketsByProduct;
}

export function initGame(options: InitGameOptions): FishShopV2State {
  assertValidPlayerIds(options.playerIds, options.hostPlayerId);

  return {
    gameId: options.gameId ?? `fish-shop-v2-${options.playerIds.join("-")}`,
    status: "IN_PROGRESS",
    roundCount: options.roundCount ?? DEFAULT_ROUND_COUNT,
    currentRound: createRound(1),
    completedRounds: [],
    players: options.playerIds.map((playerId) => ({
      playerId,
      remainingTickets: SALES_TICKETS_PER_PLAYER,
      totalIncomeWon: 0,
      itemStatus: "AVAILABLE",
      checkSnapshotWon: null,
    })),
    winnerIds: [],
    lowestPlayerIds: [],
    garnetAwardsByPlayerId: {},
    restartNumber: 0,
    hostPlayerId: options.hostPlayerId,
    seed: options.seed ?? getDefaultSeed(options.playerIds),
  };
}
